import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Form, Row, Col, Tabs, Tab, Table, Spinner, Badge } from 'react-bootstrap';
import Button from 'components/base/Button';
import api from '../../../../services/api';

interface ParameterItem {
  name: string;
  type: string;
  description: string;
  default_value: string;
}

interface ResponseMappingEntry {
  id: string;
  path: string;
  type: 'string' | 'number';
  parameter_name: string;
  description: string;
  is_array: boolean;
}

interface ResponseMappingConfig {
  response_type: 'none' | 'string' | 'file' | 'json' | 'xml';
  parameter_name?: string;
  description?: string;
  node_file_name?: string;
  file_parameter_name?: string;
  file_description?: string;
  mappings?: ResponseMappingEntry[];
}

interface OutputParameter {
  name: string;
  mapping: string;
  value: any;
  error: string | null;
}

interface TestResult {
  success: boolean;
  error?: string;
  http_status: number | null;
  headers: Record<string, string>;
  body: any;
  output_parameters: OutputParameter[];
}

interface TestIntegrationNodeModalProps {
  show: boolean;
  onHide: () => void;
  integrationId: string;
  nodeId: string;
  parameters: ParameterItem[];
  responseMapping: ResponseMappingConfig;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  value: string;
}

const TestIntegrationNodeModal: React.FC<TestIntegrationNodeModalProps> = ({ show, onHide, integrationId, nodeId, parameters, responseMapping }) => {
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [authType, setAuthType] = useState<'none' | 'basic' | 'bearer'>('none');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [resultTab, setResultTab] = useState('body');
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    value: '',
  });

  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Initialize param values with defaults when modal opens
  useEffect(() => {
    if (show) {
      const defaults: Record<string, string> = {};
      parameters.forEach((p) => {
        defaults[p.name] = p.default_value || '';
      });
      setParamValues(defaults);
      setTestResult(null);
      setResultTab('body');
    }
  }, [show, parameters]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu((prev) => ({ ...prev, visible: false }));
    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.visible]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const auth =
        authType === 'basic'
          ? { type: 'basic', username: authUsername, password: authPassword }
          : authType === 'bearer'
            ? { type: 'bearer', token: authToken }
            : null;

      const res = await api.post(`/integrations/${integrationId}/nodes/${nodeId}/test`, {
        parameter_values: paramValues,
        auth,
        request_body: requestBody || null,
      });
      setTestResult(res.data);
      setResultTab('body');
    } catch (err: any) {
      setTestResult({
        success: false,
        error: err?.response?.data?.error || err.message,
        http_status: null,
        headers: {},
        body: null,
        output_parameters: [],
      });
    } finally {
      setTesting(false);
    }
  };

  const handleContextMenu = useCallback((e: React.MouseEvent, value: string) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      value: String(value ?? ''),
    });
  }, []);

  const handleCopyValue = useCallback(() => {
    navigator.clipboard.writeText(contextMenu.value);
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, [contextMenu.value]);

  const formatValue = (param: OutputParameter): React.ReactNode => {
    if (param.error) {
      return <span className="text-danger">{param.error}</span>;
    }
    if (param.value === null || param.value === undefined) {
      return <span className="text-muted">{'{{}}'}</span>;
    }
    if (param.value === '') {
      return null; // Empty cell
    }
    if (Array.isArray(param.value)) {
      return param.value.map((v) => String(v)).join(', ');
    }
    if (typeof param.value === 'object') {
      return JSON.stringify(param.value);
    }
    return String(param.value);
  };

  const getRawValue = (param: OutputParameter): string => {
    if (param.error) return param.error;
    if (param.value === null || param.value === undefined) return '{{}}';
    if (param.value === '') return '';
    if (Array.isArray(param.value)) return param.value.map((v) => String(v)).join(', ');
    if (typeof param.value === 'object') return JSON.stringify(param.value);
    return String(param.value);
  };

  const formatResponseBody = (body: any): string => {
    if (body === null || body === undefined) return '';
    if (typeof body === 'string') {
      // Try to pretty-print if JSON string
      try {
        return JSON.stringify(JSON.parse(body), null, 2);
      } catch {
        return body;
      }
    }
    if (typeof body === 'object') {
      return JSON.stringify(body, null, 2);
    }
    return String(body);
  };

  const hasOutputParameters =
    responseMapping.response_type === 'string' ||
    ((responseMapping.response_type === 'json' || responseMapping.response_type === 'xml') && (responseMapping.mappings || []).length > 0);

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Test Integration Node</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Parameter Inputs */}
          {parameters.length > 0 && (
            <div className="mb-4">
              <h6>Parameter Values</h6>
              <small className="text-muted d-block mb-2">Provide test values for parameters used in the URL and headers.</small>
              <Row className="g-2">
                {parameters.map((p) => (
                  <Col key={p.name} xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className="mb-1">
                        <small>{p.name}</small>
                        {p.type !== 'String' && (
                          <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>
                            {p.type}
                          </Badge>
                        )}
                      </Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder={p.description || `Value for ${p.name}`}
                        value={paramValues[p.name] || ''}
                        onChange={(e) =>
                          setParamValues((prev) => ({
                            ...prev,
                            [p.name]: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Auth */}
          <div className="mb-4">
            <h6>Authentication</h6>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={3}>
                <Form.Select size="sm" value={authType} onChange={(e) => setAuthType(e.target.value as any)}>
                  <option value="none">No Auth</option>
                  <option value="basic">Basic Auth</option>
                  <option value="bearer">Bearer Token</option>
                </Form.Select>
              </Col>
              {authType === 'basic' && (
                <>
                  <Col xs={12} md={4}>
                    <Form.Control size="sm" type="text" placeholder="Username" value={authUsername} onChange={(e) => setAuthUsername(e.target.value)} />
                  </Col>
                  <Col xs={12} md={5}>
                    <Form.Control size="sm" type="password" placeholder="Password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} />
                  </Col>
                </>
              )}
              {authType === 'bearer' && (
                <Col xs={12} md={9}>
                  <Form.Control size="sm" type="password" placeholder="Bearer token" value={authToken} onChange={(e) => setAuthToken(e.target.value)} />
                </Col>
              )}
            </Row>
          </div>

          {/* Request Body */}
          <div className="mb-4">
            <h6>Request Body</h6>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              placeholder="Optional request body (JSON, XML, etc.)"
              value={requestBody}
              onChange={(e) => setRequestBody(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </div>

          {/* Send Button */}
          <div className="d-flex align-items-center gap-3 mb-4">
            <Button variant="primary" onClick={handleTest} disabled={testing}>
              {testing ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" />
                  Sending...
                </>
              ) : (
                'Send Request'
              )}
            </Button>
            {testResult && (
              <div>
                {testResult.success ? (
                  <Badge bg={testResult.http_status && testResult.http_status < 400 ? 'success' : 'warning'}>HTTP {testResult.http_status}</Badge>
                ) : (
                  <Badge bg="danger">Error</Badge>
                )}
              </div>
            )}
          </div>

          {/* Results */}
          {testResult && (
            <Tabs activeKey={resultTab} onSelect={(k) => setResultTab(k || 'body')}>
              <Tab eventKey="body" title="Response Body">
                <div className="mt-3">
                  {testResult.error && !testResult.success && <div className="text-danger mb-2">{testResult.error}</div>}
                  {testResult.headers && Object.keys(testResult.headers).length > 0 && (
                    <details className="mb-3">
                      <summary className="text-muted" style={{ cursor: 'pointer' }}>
                        Response Headers
                      </summary>
                      <pre
                        className="bg-light p-2 rounded mt-1"
                        style={{
                          fontSize: '0.8rem',
                          maxHeight: '150px',
                          overflow: 'auto',
                        }}
                      >
                        {Object.entries(testResult.headers)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join('\n')}
                      </pre>
                    </details>
                  )}
                  <pre
                    className="bg-light p-3 rounded"
                    style={{
                      fontSize: '0.85rem',
                      maxHeight: '400px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      userSelect: 'text',
                    }}
                  >
                    {formatResponseBody(testResult.body)}
                  </pre>
                </div>
              </Tab>

              <Tab eventKey="output" title="Response Output Parameters">
                <div className="mt-3">
                  {!hasOutputParameters ? (
                    <div className="text-muted py-4 text-center">
                      No output parameters available. Set the response type to String, JSON or XML on the Response tab and define a parameter for the response
                      body or a mapping to see their values displayed here.
                    </div>
                  ) : testResult.output_parameters.length === 0 ? (
                    <div className="text-muted py-4 text-center">No output parameters were resolved from this response.</div>
                  ) : (
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th style={{ width: '25%' }}>Name</th>
                          <th style={{ width: '30%' }}>Mapping</th>
                          <th style={{ width: '45%' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testResult.output_parameters.map((param, i) => (
                          <tr key={i}>
                            <td style={{ userSelect: 'text' }} onContextMenu={(e) => handleContextMenu(e, param.name)}>
                              {param.name}
                            </td>
                            <td
                              style={{
                                userSelect: 'text',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                              }}
                              onContextMenu={(e) => handleContextMenu(e, param.mapping)}
                            >
                              {param.mapping}
                            </td>
                            <td
                              style={{
                                userSelect: 'text',
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              onContextMenu={(e) => handleContextMenu(e, getRawValue(param))}
                              title={getRawValue(param)}
                            >
                              {formatValue(param)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '4px 0',
            minWidth: '140px',
          }}
        >
          <div
            style={{
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
            className="hover-bg-light"
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={handleCopyValue}
          >
            Copy Value
          </div>
        </div>
      )}
    </>
  );
};

export default TestIntegrationNodeModal;
